import { toast } from 'sonner';
import { startTransition } from 'react';
import { useMutation } from '@apollo/client/react';

import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AppMembershipRoleEnum, UserRoleEnum } from 'src/graphql/schema';
import { AppMembershipUpdateDocument } from 'src/graphql/actions/appMemberships/appMembershipUpdate.mutation';
import { cn } from 'src/lib/utils';

import { roleOptions, roleOptionsDict } from '../model/roleOptions';
import { refetchAppMemberships } from '../api';

const RoleCell = ({
  role,
  id,
  canUpdate = false,
  userRole,
}: {
  role: string;
  id: string;
  canUpdate?: boolean;
  userRole?: UserRoleEnum;
}) => {
  const [updateAppMembership, { loading }] = useMutation(AppMembershipUpdateDocument);

  const onUpdate = async (role: AppMembershipRoleEnum) => {
    try {
      await updateAppMembership({
        variables: {
          input: {
            appMembershipId: id,
            appMembershipParams: {
              role,
            },
          },
        },
      });
      toast.success('Member access has been updated');
      startTransition(() => refetchAppMemberships());
    } catch (error) {
      toast.error('Failed to update member access');
    }
  };

  if (userRole === UserRoleEnum.Owner)
    return (
      <Badge variant="pill" className="whitespace-nowrap bg-foreground text-white">
        Account owner
      </Badge>
    );

  if (canUpdate) {
    return (
      <Select value={role} onValueChange={onUpdate}>
        <SelectTrigger variant="ghost" justify="end" disabled={loading} className="px-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {roleOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Badge
      variant="pill"
      className={cn('font-semibold border-0 py-1', {
        'bg-sky-100 text-sky-700': role === AppMembershipRoleEnum.Manager,
        'bg-muted text-slate-700': role === AppMembershipRoleEnum.Viewer,
      })}
    >
      {roleOptionsDict[role as AppMembershipRoleEnum]}
    </Badge>
  );
};

export default RoleCell;
